import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface Mode1Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
  receptionAccount: string
  diningChoice: 'takeaway' | 'dinein' | 'both'
  startupMessage: boolean
  startupMessageText: string
  activateHeuchelbergMode: boolean
  showEmailField: boolean
  hideMobileField: boolean
  showNameOnBasket: boolean
  enablePayment: boolean
  waitingTimeAlgorithm: string
  waitingTimeAlgorithmValue: number
  minMinutesBetweenOrders: number
  activateTipOption: boolean
  orderConfirmationMessage: string
}

export interface Mode2Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
  locationIdentifier: string
  showEmailField: boolean
  showNameOnBasket: boolean
  showWishesComments: string
  enablePayment: boolean
  rateLimitingEnabled: boolean
  maxAmountPerOrder: number
  timeBetweenOrders: number
  maxOrderPerDay: number
  qrPaymentEnabled: boolean
  bookAsTakeaway: boolean
}

export interface Mode4Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
  baseDeliveryFee: number
  pricePerKilometre: number
  maxDeliveryDistance: number
  timeOperationalEnabled: boolean
  timeOperational: Record<string, TimeSlot[]>
}

export interface Mode5Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
}

export interface OrderModesState {
  mode1: Mode1Settings
  mode2: Mode2Settings
  mode4: Mode4Settings
  mode5: Mode5Settings
}

const defaultTimeSlots = {
  Monday: [{ startTime: '09:00', endTime: '22:00' }],
  Tuesday: [{ startTime: '09:00', endTime: '22:00' }],
  Wednesday: [{ startTime: '09:00', endTime: '22:00' }],
  Thursday: [{ startTime: '09:00', endTime: '22:00' }],
  Friday: [{ startTime: '09:00', endTime: '23:00' }],
  Saturday: [{ startTime: '10:00', endTime: '23:00' }],
  Sunday: [{ startTime: '10:00', endTime: '22:00' }],
}

export const useOrderModesStore = defineStore('orderModes', () => {
  const orderModes = ref<OrderModesState>({
    mode1: {
      enabled: false,
      label: 'Self Service',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
      receptionAccount: '',
      diningChoice: 'both',
      startupMessage: false,
      startupMessageText: '',
      activateHeuchelbergMode: false,
      showEmailField: true,
      hideMobileField: false,
      showNameOnBasket: false,
      enablePayment: false,
      waitingTimeAlgorithm: 'default',
      waitingTimeAlgorithmValue: 0,
      minMinutesBetweenOrders: 0,
      activateTipOption: false,
      orderConfirmationMessage: '',
    },
    mode2: {
      enabled: false,
      label: 'Self Ordering',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
      locationIdentifier: '',
      showEmailField: true,
      showNameOnBasket: false,
      showWishesComments: '',
      enablePayment: false,
      rateLimitingEnabled: false,
      maxAmountPerOrder: 0,
      timeBetweenOrders: 0,
      maxOrderPerDay: 0,
      qrPaymentEnabled: false,
      bookAsTakeaway: false,
    },
    mode4: {
      enabled: false,
      label: 'Delivery',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
      baseDeliveryFee: 0,
      pricePerKilometre: 0,
      maxDeliveryDistance: 0,
      timeOperationalEnabled: false,
      timeOperational: { ...defaultTimeSlots },
    },
    mode5: {
      enabled: false,
      label: 'Kiosk (Terminals)',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
    },
  })

  const updateMode1 = (data: Partial<Mode1Settings>) => {
    orderModes.value.mode1 = { ...orderModes.value.mode1, ...data }
  }

  const updateMode2 = (data: Partial<Mode2Settings>) => {
    orderModes.value.mode2 = { ...orderModes.value.mode2, ...data }
  }

  const updateMode4 = (data: Partial<Mode4Settings>) => {
    orderModes.value.mode4 = { ...orderModes.value.mode4, ...data }
  }

  const updateMode5 = (data: Partial<Mode5Settings>) => {
    orderModes.value.mode5 = { ...orderModes.value.mode5, ...data }
  }

  const getOrderModes = () => orderModes.value

  const reset = () => {
    orderModes.value = {
      mode1: {
        enabled: false,
        label: 'Self Service',
        minimumOrderAmount: 0,
        priceLevel: 'standard',
        receptionAccount: '',
        diningChoice: 'both',
        startupMessage: false,
        startupMessageText: '',
        activateHeuchelbergMode: false,
        showEmailField: true,
        hideMobileField: false,
        showNameOnBasket: false,
        enablePayment: false,
        waitingTimeAlgorithm: 'default',
        waitingTimeAlgorithmValue: 0,
        minMinutesBetweenOrders: 0,
        activateTipOption: false,
        orderConfirmationMessage: '',
      },
      mode2: {
        enabled: false,
        label: 'Self Ordering',
        minimumOrderAmount: 0,
        priceLevel: 'standard',
        locationIdentifier: '',
        showEmailField: true,
        showNameOnBasket: false,
        showWishesComments: '',
        enablePayment: false,
        rateLimitingEnabled: false,
        maxAmountPerOrder: 0,
        timeBetweenOrders: 0,
        maxOrderPerDay: 0,
        qrPaymentEnabled: false,
        bookAsTakeaway: false,
      },
      mode4: {
        enabled: false,
        label: 'Delivery',
        minimumOrderAmount: 0,
        priceLevel: 'standard',
        baseDeliveryFee: 0,
        pricePerKilometre: 0,
        maxDeliveryDistance: 0,
        timeOperationalEnabled: false,
        timeOperational: { ...defaultTimeSlots },
      },
      mode5: {
        enabled: false,
        label: 'Kiosk (Terminals)',
        minimumOrderAmount: 0,
        priceLevel: 'standard',
      },
    }
  }

  return {
    orderModes,
    updateMode1,
    updateMode2,
    updateMode4,
    updateMode5,
    getOrderModes,
    reset,
  }
})
